/*
 * Shared helpers for credentials stored in the aquacred service.
 *
 * aquacred keys every credential by (service, cid, namespace, name). For the
 * `amdp` service the namespace is a sandbox name, every other service uses a
 * plain namespace string.
 */

// Services whose credentials are managed through aquacred.
// `namespaceType: "sandbox"` renders the namespace picker as a sandbox dropdown,
// `namespaceType: "text"` falls back to a free-form namespace input.
// Supported services → amdp,pail,audsum,aae,lakeview,aut,surface
const AQUACRED_SERVICES = [
    { label: "aMDP", value: "amdp", namespaceType: "sandbox", namespaceLabel: "Sandbox" },
    { label: "Pail", value: "pail", namespaceType: "text", namespaceLabel: "Namespace", namespaceDefault: "default", disabled: true },
    { label: "Audsum", value: "audsum", namespaceType: "text", namespaceLabel: "Namespace", namespaceDefault: "default", disabled: true },
    { label: "Analytics Engine", value: "aae", namespaceType: "text", namespaceLabel: "Namespace", namespaceDefault: "default", disabled: true },
    { label: "Lakeview", value: "lakeview", namespaceType: "text", namespaceLabel: "Namespace", namespaceDefault: "default", disabled: true },
    { label: "aUT", value: "aut", namespaceType: "text", namespaceLabel: "Namespace", namespaceDefault: "default", disabled: true },
    { label: "Surface", value: "surface", namespaceType: "text", namespaceLabel: "Namespace", namespaceDefault: "default", disabled: true },
    // credentials the pail file browser reads buckets/objects with
    { label: "File Browser", value: "filebrowser", namespaceType: "text", namespaceLabel: "Namespace", namespaceDefault: "default" },
];

const DEFAULT_AQUACRED_SERVICE = "amdp";

// All credential types supported by aquacred (see https://github.com/aqfer/adl-sdk/blob/master/jobs/cred.md).
const CREDENTIAL_TYPES = [
    { label: "Aqfer", value: "aqfer" },
    { label: "API Key", value: "api_key" },
    { label: "Auth (deprecated)", value: "auth" },
    { label: "AWS", value: "aws" },
    { label: "Azure", value: "azure" },
    { label: "Basic", value: "basic" },
    { label: "Bing Ads", value: "bing_ads" },
    { label: "Boostr", value: "boostr" },
    { label: "Comscore", value: "comscore" },
    { label: "Databricks", value: "databricks" },
    { label: "Facebook", value: "facebook" },
    { label: "Freewheel", value: "freewheel" },
    { label: "FTP", value: "ftp" },
    { label: "GCP", value: "gcp" },
    { label: "Google Ads", value: "google_ads" },
    { label: "Hash Salt", value: "hash_salt" },
    { label: "Hash Key", value: "hash_key" },
    { label: "Nielsen", value: "nielsen" },
    { label: "PAPI", value: "papi" },
    { label: "RSA Key Pair", value: "rsa_key_pair" },
    { label: "SSH", value: "ssh" },
    { label: "Trade Desk", value: "trade_desk" },
    { label: "YouTube Analytics V2", value: "youtube_analytics_v2" },
];

// aquacred marks a field as sensitive by returning its value wrapped as
// `${...}` (a reference into the secret store) instead of the real value -
// any field can be sensitive depending on credential type, so we detect it
// from the value shape rather than a fixed field-name list.
const MASKED_VALUE_PATTERN = /^\$\{.*\}$/;

const MASKED_VALUE = '***********';

function isMaskedValue(value) {
    return typeof value === 'string' && MASKED_VALUE_PATTERN.test(value);
}

// Fields required for each credential type - drives both add/edit validation
// and which fields are copied into the write payload.
const REQUIRED_CREDENTIAL_FIELDS = {
    api_key: ['api_key'],
    aqfer: ['refresh_token'],
    auth: ['refresh_token'],
    aws: ['access_key', 'secret_key'],
    azure: ['account_name', 'shared_signature'],
    basic: ['username', 'password'],
    bing_ads: ['developer_token', 'client_id', 'client_state', 'refresh_token'],
    boostr: ['username', 'password'],
    comscore: ['username', 'password'],
    databricks: ['workspace_url', 'client_id', 'secret'],
    facebook: [],
    freewheel: ['username', 'password'],
    ftp: ['username', 'password'],
    gcp: ['project_id', 'private_key_id', 'private_key', 'client_email', 'client_id', 'auth_uri', 'token_uri', 'auth_provider_x509_cert_url', 'client_x509_cert_url'],
    // client_secret/refresh_token (API access) or the GCP service-account fields
    // (project_id..service_account_user) each independently satisfy google_ads -
    // only fields required under both paths are listed here.
    google_ads: ['client_id', 'developer_token', 'login_customer_id', 'linked_customer_id'],
    hash_salt: ['salt_id', 'salt'],
    hash_key: ['pepper'],
    nielsen: ['username', 'password', 'api_key'],
    papi: ['host', 'client_token', 'client_secret', 'access_token'],
    rsa_key_pair: [],
    ssh: ['username'],
    trade_desk: ['username', 'password'],
    youtube_analytics_v2: ['project_id', 'private_key_id', 'private_key', 'client_email', 'client_id', 'auth_uri', 'token_uri', 'auth_provider_x509_cert_url', 'client_x509_cert_url', 'content_owner_id'],
};

// Keys that uniquely identify a credential type, checked in order - used only
// as a fallback for credentials stored before `type` was persisted. Order
// matters: more specific/rarer shapes are checked before broader ones.
const CREDENTIAL_TYPE_SIGNATURES = [
    { type: 'youtube_analytics_v2', keys: ['content_owner_id'] },
    { type: 'google_ads', keys: ['developer_token', 'login_customer_id'] },
    { type: 'gcp', keys: ['project_id', 'private_key_id', 'client_email'] },
    { type: 'bing_ads', keys: ['developer_token', 'client_state'] },
    { type: 'papi', keys: ['client_token', 'client_secret', 'access_token'] },
    { type: 'facebook', keys: ['app_id'] },
    { type: 'facebook', keys: ['ad_account_id'] },
    { type: 'databricks', keys: ['workspace_url'] },
    { type: 'azure', keys: ['account_name', 'shared_signature'] },
    { type: 'ssh', keys: ['username', 'private_key'] },
    { type: 'ssh', keys: ['username', 'timeout'] },
    { type: 'rsa_key_pair', keys: ['private_key'] },
    { type: 'aws', keys: ['access_key', 'secret_key'] },
    { type: 'aws', keys: ['role_arn'] },
    { type: 'api_key', keys: ['api_key'] },
    { type: 'hash_salt', keys: ['salt_id', 'salt'] },
    { type: 'hash_key', keys: ['pepper'] },
    { type: 'aqfer', keys: ['refresh_token'] },
    { type: 'basic', keys: ['username', 'password'] },
];

// aquacred returns the `type` field for anything written through this UI - this
// inference only covers credentials that predate that field being persisted.
function getCredentialType(credential) {
    if (credential?.type) return credential.type;
    let keys = Object.keys(credential || {});
    if (!keys.length) return null;
    const match = CREDENTIAL_TYPE_SIGNATURES.find((sig) => sig.keys.every((key) => keys.includes(key)));
    return match ? match.type : null;
}

function getServiceConfig(service) {
    return AQUACRED_SERVICES.find((s) => s.value === service) || {
        // unknown/newly added service - treat the namespace as free-form text
        label: service,
        value: service,
        namespaceType: "text",
        namespaceLabel: "Namespace",
    };
}

// Replaces every `${...}`-shaped value with the display mask, and remembers
// which fields were sensitive (on `_sensitiveFields`) so a later cancel/edit
// can re-mask or clear them once the `${...}` shape itself is gone.
function maskSensitiveFields(credential) {
    if (!credential) return credential;
    const sensitiveFields = new Set(credential._sensitiveFields || []);
    Object.keys(credential).forEach((field) => {
        if (isMaskedValue(credential[field])) {
            sensitiveFields.add(field);
            credential[field] = MASKED_VALUE;
        }
    });
    credential._sensitiveFields = [...sensitiveFields];
    return credential;
}

function clearSensitiveFields(credential) {
    if (!credential) return credential;
    (credential._sensitiveFields || []).forEach((field) => {
        if (credential[field]) credential[field] = '';
    });
    return credential;
}

function hasRequiredFields(credential) {
    if (!credential || !credential.type) return false;
    let required = REQUIRED_CREDENTIAL_FIELDS[credential.type] || [];
    return required.every((field) => credential[field]);
}

export {
    AQUACRED_SERVICES,
    CREDENTIAL_TYPES,
    DEFAULT_AQUACRED_SERVICE,
    REQUIRED_CREDENTIAL_FIELDS,
    MASKED_VALUE,
    isMaskedValue,
    getCredentialType,
    getServiceConfig,
    maskSensitiveFields,
    clearSensitiveFields,
    hasRequiredFields,
};
